<script setup lang="ts">
/**
 * The index of what the document discloses.
 *
 * Built from the ESRS tags on the sections around it whenever the document
 * changes, exactly as the contents page is built from the headings. Storing
 * the entries would mean an index that goes quietly wrong the first time
 * somebody moves a chapter, and an index that sends an auditor to the wrong
 * page is worse than no index at all.
 *
 * Grouped by standard, in the order a statement presents them, because that is
 * how somebody looks for a requirement. Standards nothing is tagged to are
 * left out: an index listing all eleven with most of them empty says less than
 * one listing what is actually there.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { disclosureIndex } from '@/domain/esrsIndex'
import type { DisclosureIndexSection } from '@/domain/section'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

const props = defineProps<{ section: DisclosureIndexSection }>()

const report = useReportStore()

const entries = computed(() => disclosureIndex(report.sections))
</script>

<template>
  <nav class="index" :aria-label="section.title">
    <p v-if="entries.length === 0" class="index__empty">{{ t('inspector.index.empty') }}</p>

    <div v-for="entry in entries" :key="entry.standard" class="index__standard">
      <p class="index__name">
        <span class="index__code">{{
          entry.standard === 'ESRS2' ? 'ESRS 2' : entry.standard
        }}</span>
        {{ t(`esrs.standards.${entry.standard}`) }}
      </p>

      <ul class="index__rows">
        <li v-for="disclosure in entry.disclosures" :key="disclosure.sectionId" class="index__row">
          <span class="index__ref">{{ disclosure.ref }}</span>
          <span v-if="props.section.showTitles" class="index__title">{{ disclosure.title }}</span>
          <span v-if="disclosure.materiality !== 'material'" class="index__state">{{
            t(`esrs.materialities.${disclosure.materiality}`)
          }}</span>
        </li>
      </ul>
    </div>
  </nav>
</template>

<style scoped>
.index {
  margin: 2mm 0 3mm;
}

.index__empty {
  margin: 0;
  color: var(--paper-ink-soft);
  font-style: italic;
  text-align: left;
}

.index__standard + .index__standard {
  margin-top: 4mm;
}

.index__name {
  margin: 0 0 1.5mm;
  padding-bottom: 1mm;
  border-bottom: 1px solid var(--paper-rule);
  font-size: 9.5pt;
  font-weight: 600;
}

/* The code in the accent and in a column of its own width, so a run of
   standards lines up and can be scanned rather than read. */
.index__code {
  display: inline-block;
  min-width: 16mm;
  color: var(--page-accent);
}

.index__rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.index__row {
  display: flex;
  align-items: baseline;
  gap: 2mm;
  margin-bottom: 0.8mm;
  font-size: 9.5pt;
}

.index__ref {
  flex: none;
  min-width: 16mm;
  font-variant-numeric: tabular-nums;
}

.index__title {
  flex: 1;
  min-width: 0;
}

/* Only where it is not the ordinary case. A row reading "material" on every
   line of a long index is a column of noise. */
.index__state {
  flex: none;
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
  font-style: italic;
}
</style>
